<template>
  <div v-if="!trait._destroy" class="relative mb-8 rounded-sm border border-solid border-main p-4 light-theme">
    <ChipBadge class="absolute -top-3.5 right-4 flex" color="purple" rounded>
      <button :disabled="disabled" class="mr-1" data-testid="agency-trait-delete-button" @click="$emit('delete', index)">
        <MdiSvg :size="18" :path="mdiTrashCanOutline" />
      </button>
      <button
        :disabled="disabled || trait.position === 1"
        class="mr-1"
        :class="{ 'text-primary-subtle': trait.position === 1 }"
        data-testid="agency-trait-move-up"
        @click="$emit('moveUp', index)"
      >
        <MdiSvg :size="18" :path="mdiArrowUp" />
      </button>
      <button
        :disabled="disabled || index === total"
        class="mr-1"
        :class="{ 'text-primary-subtle': index === total }"
        data-testid="agency-trait-move-down"
        @click="$emit('moveDown', index)"
      >
        <MdiSvg :size="18" :path="mdiArrowDown" />
      </button>
    </ChipBadge>
    <div class="mb-6">
      <RadioButton
        v-model="v$.kind.$model"
        class="mr-4"
        value="certificate"
        :disabled="disabled"
        data-testid="agency-trait-radio-certificate"
      >
        {{ t('Reviews.agency.edit.traits.form.cert.label') }}
      </RadioButton>
      <RadioButton v-model="v$.kind.$model" value="usp" :disabled="disabled" data-testid="agency-trait-radio-usp">
        {{ t('Reviews.agency.edit.traits.form.usp.label') }}
      </RadioButton>
    </div>
    <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="pb-12">
      <VuelidateField :validation="v$.title[lang]">
        <template #default="{ error, state, countHint }">
          <FieldWrapper :for="`traits-title-input-${lang}`" :error="error" :count-hint="countHint">
            <template #label>
              <div class="flex items-center justify-between">
                <span>{{ t('Reviews.agency.edit.traits.form.input.titleLabel') }}</span>
                <ChipBadge class="uppercase" color="purple" rounded size="s">{{ lang }}</ChipBadge>
              </div>
            </template>
            <template #default="{ id }">
              <div class="mt-2">
                <InputField
                  :id="id"
                  v-model="v$.title[lang].$model"
                  :data-testid="`traits-title-input-${lang}`"
                  type="text"
                  :state="state"
                  :disabled="disabled"
                  :maxlength="TITLE_MAX_LENGTH"
                  :placeholder="t('Reviews.agency.edit.traits.form.input.placeholderTitle').toString()"
                />
              </div>
            </template>
          </FieldWrapper>
        </template>
      </VuelidateField>
      <VuelidateField :validation="v$.description[lang]">
        <template #default="{ error, state, countHint }">
          <FieldWrapper :for="`traits-description-input-${lang}`" :error="error" :count-hint="countHint">
            <template #label>
              <div class="mt-2 flex items-center justify-between">
                <span>{{ t('Reviews.agency.edit.traits.form.input.descriptionLabel') }}</span>
              </div>
            </template>
            <template #default="{ id }">
              <div class="mt-2">
                <InputField
                  :id="id"
                  v-model="v$.description[lang].$model"
                  :data-testid="`traits-description-input-${lang}`"
                  type="text"
                  :state="state"
                  :disabled="disabled"
                  :maxlength="DESCRIPTION_MAX_LENGTH"
                  :placeholder="t('Reviews.agency.edit.traits.form.input.placeholderDescription').toString()"
                />
              </div>
            </template>
          </FieldWrapper>
        </template>
      </VuelidateField>
    </div>
  </div>
</template>
<script setup lang="ts">
import { mdiArrowDown, mdiArrowUp, mdiTrashCanOutline } from '@mdi/js'
import { MdiSvg, RadioButton, ChipBadge, FieldWrapper, InputField } from '@ramp106/omrjs-core-ui'
import { useVuelidate } from '@vuelidate/core'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { AgencyTranslatedTrait } from '@/gql/reviews/types'
import { ALLOWED_LOCALES } from '@/helpers/translationsHelper'
import { buildTranslationFieldsRules } from '@/helpers/validationHelper'
import { maxLength, minLength, required } from '@/services/validators'
import VuelidateField from '@/ui/VuelidateField.vue'

const props = defineProps<{
  trait: AgencyTranslatedTrait
  index: number
  total: number
  disabled?: boolean
}>()

const INPUT_MIN_LENGTH = 3
const TITLE_MAX_LENGTH = 40
const DESCRIPTION_MAX_LENGTH = 80
defineEmits(['delete', 'moveUp', 'moveDown'])
const { t } = useI18n()

const rules = computed(() => ({
  position: { required },
  kind: { required },
  title: {
    ...buildTranslationFieldsRules(
      {
        required,
        minLength: minLength(INPUT_MIN_LENGTH),
        maxLength: maxLength(TITLE_MAX_LENGTH),
      },
      [ALLOWED_LOCALES[0]],
    ),
    ...buildTranslationFieldsRules(
      {
        minLength: minLength(INPUT_MIN_LENGTH),
        maxLength: maxLength(TITLE_MAX_LENGTH),
      },
      ALLOWED_LOCALES.slice(1),
    ),
  },
  description: {
    ...buildTranslationFieldsRules(
      {
        required,
        minLength: minLength(INPUT_MIN_LENGTH),
        maxLength: maxLength(DESCRIPTION_MAX_LENGTH),
      },
      [ALLOWED_LOCALES[0]],
    ),
    ...buildTranslationFieldsRules(
      {
        minLength: minLength(INPUT_MIN_LENGTH),
        maxLength: maxLength(DESCRIPTION_MAX_LENGTH),
      },
      ALLOWED_LOCALES.slice(1),
    ),
  },
}))

const v$ = useVuelidate(rules, props.trait)
</script>
